(() => {
  if (customElements.get('yahav-collection-mobile-header')) return;

  class YahavCollectionMobileHeader extends HTMLElement {
    constructor() {
      super();
      this.frame = 0;
      this.compact = false;
      this.fullHeaderHeight = 0;
      this.needsMeasurement = true;
      this.scheduleUpdate = this.scheduleUpdate.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleSectionChange = this.handleSectionChange.bind(this);
      this.openFilters = this.openFilters.bind(this);
      this.closeFilters = this.closeFilters.bind(this);
    }

    connectedCallback() {
      if (this.listening || !document.body.classList.contains('template-collection')) return;
      this.root = this.closest('.ycol');
      if (!this.root) return;

      this.listening = true;
      this.mobile = window.matchMedia('(max-width: 749px)');
      window.addEventListener('scroll', this.scheduleUpdate, { passive: true });
      window.addEventListener('resize', this.handleResize);
      this.mobile.addEventListener('change', this.handleResize);
      document.addEventListener('shopify:section:load', this.handleSectionChange);
      document.addEventListener('shopify:section:unload', this.handleSectionChange);
      this.lockObserver = new MutationObserver(this.scheduleUpdate);
      this.lockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-scroll-locked', 'class'] });
      this.scheduleUpdate();
    }

    scheduleUpdate() {
      if (!this.listening || this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        if (this.isConnected) this.update();
      });
    }

    handleResize() {
      this.needsMeasurement = true;
      this.scheduleUpdate();
    }

    handleSectionChange(event) {
      if (event.type === 'shopify:section:unload' && event.target.contains(this)) {
        this.disconnectedCallback();
        return;
      }
      if (this.header && event.target.contains(this.header)) this.releaseHeader();
      this.handleResize();
    }

    bindHeader(header) {
      const mobileHeader = header?.querySelector('.header__mobile');
      const mobileRight = mobileHeader?.querySelector('.header__mobile__right');
      const heightElement = header?.querySelector('[data-header-height]');
      if (!mobileHeader || !mobileRight || !heightElement) return;

      this.header = header;
      this.heightElement = heightElement;
      this.needsMeasurement = true;

      // Add only the collection shortcut; Broadcast retains all native header controls.
      const drawer = this.root.querySelector('[data-ycol-filters]');
      const originalButton = this.root.querySelector('[data-ycol-open-filters]');
      const template = this.querySelector('template[data-ycol-mobile-filter-template]');
      const templateButton = template?.content.querySelector('button.ycol-mobile-filter');
      if (drawer && typeof drawer.showModal === 'function' && originalButton && templateButton) {
        this.drawer = drawer;
        this.originalButton = originalButton;
        this.filterButton = templateButton.cloneNode(true);
        this.filterButton.hidden = true;
        this.filterButton.type = 'button';
        this.filterButton.setAttribute('aria-haspopup', 'dialog');
        this.filterButton.setAttribute('aria-controls', drawer.id);
        this.filterButton.setAttribute('aria-expanded', 'false');
        this.account = mobileRight.querySelector('.icon-profile-circled')?.closest('.header__mobile__button');
        mobileRight.insertBefore(this.filterButton, this.account || mobileRight.firstElementChild);
        this.account?.classList.add('ycol-mobile-account');
        this.filterButton.addEventListener('click', this.openFilters);
        drawer.addEventListener('close', this.closeFilters);
      }

      if (typeof ResizeObserver === 'function') {
        this.headerObserver = new ResizeObserver(() => {
          if (!this.compact) this.needsMeasurement = true;
          this.scheduleUpdate();
        });
        this.headerObserver.observe(heightElement);
      }
    }

    update() {
      const header = document.querySelector('header-component[data-header-sticky]');
      if (header !== this.header) {
        this.releaseHeader();
        this.bindHeader(header);
      }
      const toolbar = this.root.querySelector('.ycol-toolbar');
      if (!this.header || !toolbar) return;

      const html = document.documentElement;
      if (html.hasAttribute('data-scroll-locked') || html.classList.contains('ycol-lock') || document.body.classList.contains('ycol-lock')) return;

      if (!this.mobile.matches) {
        this.setCompact(false);
        document.body.classList.remove('ycol-mobile-header-ready');
        document.body.style.removeProperty('--ycol-full-header-height');
        this.fullHeaderHeight = 0;
        this.syncNativeHeight();
        return;
      }

      if (this.needsMeasurement || !this.fullHeaderHeight) {
        // Measure the expanded header without changing its reserved page space.
        // These synchronous changes finish before the browser paints a frame.
        const wasCompact = this.compact;
        this.setCompact(false);
        this.fullHeaderHeight = this.heightElement.getBoundingClientRect().height;
        if (!this.fullHeaderHeight) return;
        document.body.style.setProperty('--ycol-full-header-height', `${this.fullHeaderHeight}px`);
        document.body.classList.add('ycol-mobile-header-ready');
        this.needsMeasurement = false;
        this.setCompact(wasCompact);
        this.syncNativeHeight();
      }

      // A transformed announcement keeps its real height and original page padding.
      const announcementHeight = document.querySelector('.wws-ab--sticky')?.offsetHeight || 0;
      const threshold = this.fullHeaderHeight + announcementHeight;
      const toolbarBottom = toolbar.getBoundingClientRect().bottom;
      const compact = window.scrollY > 1 && (this.compact ? toolbarBottom <= threshold + 24 : toolbarBottom <= threshold);
      this.setCompact(compact);
    }

    setCompact(compact) {
      const changed = this.compact !== compact;
      this.compact = compact;
      document.body.classList.toggle('ycol-mobile-shopping', compact);
      document.body.classList.toggle('ycol-mobile-offer-hidden', compact && this.dataset.hideOffer === 'true');
      if (this.filterButton) this.filterButton.hidden = !compact;
      if (changed) this.syncNativeHeight();
    }

    syncNativeHeight() {
      const heightElement = this.heightElement?.isConnected ? this.heightElement : document.querySelector('header-component [data-header-height]');
      const height = heightElement?.getBoundingClientRect().height;
      if (height) document.documentElement.style.setProperty('--header-height', `${height}px`);
    }

    openFilters() {
      if (!this.compact || !this.drawer?.isConnected || !this.originalButton?.isConnected) return;
      this.openedWithFilter = true;
      this.originalButton.click();
      this.openedWithFilter = this.drawer.open;
      this.filterButton.setAttribute('aria-expanded', String(this.drawer.open));
    }

    closeFilters() {
      this.filterButton?.setAttribute('aria-expanded', 'false');
      if (this.openedWithFilter && this.mobile.matches && this.filterButton?.isConnected && !this.filterButton.hidden) {
        this.filterButton.focus({ preventScroll: true });
      }
      this.openedWithFilter = false;
      this.scheduleUpdate();
    }

    releaseHeader() {
      this.headerObserver?.disconnect();
      this.headerObserver = null;
      this.filterButton?.removeEventListener('click', this.openFilters);
      this.filterButton?.remove();
      this.drawer?.removeEventListener('close', this.closeFilters);
      this.account?.classList.remove('ycol-mobile-account');
      this.filterButton = null;
      this.drawer = null;
      this.originalButton = null;
      this.account = null;
      this.openedWithFilter = false;
      this.setCompact(false);
      document.body.classList.remove('ycol-mobile-header-ready');
      document.body.style.removeProperty('--ycol-full-header-height');
      this.syncNativeHeight();
      this.header = null;
      this.heightElement = null;
      this.fullHeaderHeight = 0;
      this.needsMeasurement = true;
    }

    disconnectedCallback() {
      if (!this.listening) return;
      this.listening = false;
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      window.removeEventListener('scroll', this.scheduleUpdate);
      window.removeEventListener('resize', this.handleResize);
      this.mobile.removeEventListener('change', this.handleResize);
      document.removeEventListener('shopify:section:load', this.handleSectionChange);
      document.removeEventListener('shopify:section:unload', this.handleSectionChange);
      this.lockObserver?.disconnect();
      this.lockObserver = null;
      this.releaseHeader();
    }
  }

  customElements.define('yahav-collection-mobile-header', YahavCollectionMobileHeader);
})();
